import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Container,
  Form,
  Spinner,
} from "react-bootstrap";
import { ArrowLeft, ArrowRight, Phone } from "lucide-react";

import Navbar from "../components/NavbarM";

import { findOrRegisterCustomer } from "../services/memberService";

/**
 * "Add Loyalty" flow.
 *
 * The employee only enters the customer's phone number and presses Next.
 * Lookup, auto-registration (with a "Guest" placeholder name — the
 * customer sets their real name later via the customer app) and joining
 * the customer to this store all happen atomically in a single Cloud
 * Function call (storeFindOrRegisterCustomer), since members/
 * memberPhones/storeMemberships aren't directly readable or writable
 * from the client. The employee is then taken straight to Add Points.
 *
 * There is no redemption here — redemption is only allowed by scanning
 * the customer's PointsHub code.
 */
function FindCustomerPage() {
  const navigate = useNavigate();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(event) {
    event.preventDefault();

    setError("");
    setProcessing(true);

    try {
      const result = await findOrRegisterCustomer(phoneNumber);

      navigate(`/pages/add-points/${result.memberId}`, {
        state: { lookupMethod: "phone" },
      });
    } catch (submitError) {
      console.error(submitError);

      setError(
        submitError.message ||
          "The customer could not be processed. Please try again."
      );

      setProcessing(false);
    }
  }

  return (
    <>
      <Navbar />

      <Container
        className="py-4"
        style={{ maxWidth: "620px" }}
      >
        <Button
          variant="link"
          className="px-0 mb-3 text-decoration-none"
          onClick={() => navigate("/pages/home")}
        >
          <ArrowLeft size={18} className="me-2" />
          Back to Home
        </Button>

        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4 p-md-5">
            <div className="d-flex align-items-center gap-3 mb-4">
              <div
                className="d-flex align-items-center justify-content-center rounded-3"
                style={{
                  width: 52,
                  height: 52,
                  background: "#fff3e8",
                  color: "#f97316",
                }}
              >
                <Phone size={26} />
              </div>

              <div>
                <h2 className="mb-1">Add Loyalty</h2>
                <p className="text-muted mb-0">
                  Enter the customer's phone number to continue.
                </p>
              </div>
            </div>

            {error && (
              <Alert variant="danger">{error}</Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-4">
                <Form.Label>Customer phone number</Form.Label>

                <Form.Control
                  type="tel"
                  size="lg"
                  placeholder="403 555 1234"
                  value={phoneNumber}
                  onChange={(event) =>
                    setPhoneNumber(event.target.value)
                  }
                  autoFocus
                  required
                  disabled={processing}
                />

                <Form.Text>
                  New numbers are registered automatically. The customer
                  can add their name later in the customer app.
                </Form.Text>
              </Form.Group>

              <Button
                type="submit"
                size="lg"
                className="w-100"
                disabled={processing || !phoneNumber}
              >
                {processing ? (
                  <>
                    <Spinner
                      size="sm"
                      className="me-2"
                    />
                    Processing
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight size={19} className="ms-2" />
                  </>
                )}
              </Button>
            </Form>

            <Alert
              variant="light"
              className="mt-4 mb-0 border"
            >
              Phone-number lookup can only be used to earn points.
              Redemption requires scanning the customer's PointsHub code.
            </Alert>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}

export default FindCustomerPage;
